import clsx from 'clsx';

export type StepState = 'pending' | 'active' | 'done';

export type Step = {
  id: string;
  label: string;
};

export type ProgressStepsProps = {
  steps: Step[];
  currentId: string;
};

function stateFor(idx: number, currentIdx: number): StepState {
  if (idx < currentIdx) return 'done';
  if (idx === currentIdx) return 'active';
  return 'pending';
}

const bulletBase = clsx(
  'inline-flex h-4 w-4 flex-shrink-0 items-center justify-center',
  'rounded-full border-2',
);

// Figma 1061:63692: the active dot is a solid indigo (#515FFC) centre inside a
// lighter indigo halo; upcoming dots are light-grey filled circles.
const bulletStates: Record<StepState, string> = {
  pending: 'border-transparent bg-ink-200',
  active: 'border-transparent bg-[#515FFC]/20',
  done: 'border-success-fg bg-success-fg text-white',
};

// Figma 1061:63690: 12px regular labels — active is dark (#000212), upcoming
// steps are muted grey (#8289a3).
const labelStates: Record<StepState, string> = {
  pending: 'font-normal text-text-muted',
  active: 'font-normal text-text-primary',
  // Completed steps read as muted grey (#8289A3), same as upcoming ones.
  done: 'font-normal text-text-muted',
};

export function ProgressSteps({ steps, currentId }: ProgressStepsProps) {
  const currentIdx = steps.findIndex((s) => s.id === currentId);
  return (
    <ol
      role="list"
      aria-label="Progress"
      className="inline-flex w-[504px] items-center justify-center gap-2 text-sm leading-[17px] text-text-secondary"
    >
      {steps.map((s, i) => {
        const state = stateFor(i, currentIdx);
        return (
          <li
            key={s.id}
            data-state={state}
            data-step-id={s.id}
            className="inline-flex items-center gap-1"
            aria-current={state === 'active' ? 'step' : undefined}
          >
            {state === 'done' ? (
              // Finished step: a filled green check disc with a faint halo ring.
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
                className="flex-shrink-0"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="7.75"
                  fill="#576190"
                  fillOpacity="0.14"
                  stroke="#37A584"
                  strokeWidth="0.5"
                />
                <circle cx="7.89844" cy="8" r="7" fill="#38BB94" />
                <path d="M4.89844 7.6L7.14844 10L10.8984 6" stroke="white" strokeWidth="1.5" />
              </svg>
            ) : (
              <span aria-hidden="true" className={clsx(bulletBase, bulletStates[state])}>
                {state === 'active' && <span className="h-2.5 w-2.5 rounded-full bg-[#515FFC]" />}
              </span>
            )}
            <span className={labelStates[state]}>{s.label}</span>
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                data-connector
                className="ml-2 inline-block h-px w-10 border-t border-solid border-ink-300"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
